import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { GameCard } from "@/components/games/GameCard";
import type { Game } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const GAMES_QUERY = gql`
  query Games(
    $query: String
    $genre: String
    $mechanic: String
    $playerCount: Int
    $minComplexity: Float
    $maxComplexity: Float
    $minRating: Float
    $maxRating: Float
    $languageIndependent: Boolean
    $boxSize: String
    $minPrice: Float
    $maxPrice: Float
    $inStock: Boolean
    $shops: [String!]
    $sortBy: String
    $limit: Int
    $offset: Int
  ) {
    availableShops
    games(
      query: $query
      genre: $genre
      mechanic: $mechanic
      playerCount: $playerCount
      minComplexity: $minComplexity
      maxComplexity: $maxComplexity
      minRating: $minRating
      maxRating: $maxRating
      languageIndependent: $languageIndependent
      boxSize: $boxSize
      minPrice: $minPrice
      maxPrice: $maxPrice
      inStock: $inStock
      shops: $shops
      sortBy: $sortBy
      limit: $limit
      offset: $offset
    ) {
      id
      title
      imageUrl
      thumbnailUrl
      minPlayers
      maxPlayers
      playerRange
      playtimeRange
      complexity
      complexityLabel
      bggRating
      displayRating
      ratingSource
      languageIndependent
      boxSizeCategory
      activeListingsCount
      lowestPrice
      priceSparkline
      dropPercentage
      trackedShopCount
      priceHistorySummary {
        allTimeLow
        last30dLow
        currentLow
        currentLowShopName
        trackingSince
      }
    }
  }
`;

const GAME_SUGGESTIONS_QUERY = gql`
  query GameSuggestions($query: String!) {
    searchGames(query: $query) {
      id
      title
      thumbnailUrl
    }
  }
`;

const GENRES = [
  "Strategy",
  "Family",
  "Party",
  "Thematic",
  "Abstract",
  "War",
  "Economic",
  "Adventure",
  "Puzzle",
  "Trivia",
  "Card Game",
  "Dexterity",
  "Educational",
  "Horror",
  "Sci-Fi",
  "Fantasy",
  "Historical",
  "Civilization",
];

const MECHANICS = [
  "Worker Placement",
  "Deck Building",
  "Area Control",
  "Dice Rolling",
  "Hand Management",
  "Set Collection",
  "Tile Placement",
  "Auction/Bidding",
  "Cooperative",
  "Engine Building",
  "Drafting",
  "Route Building",
  "Hidden Roles",
  "Push Your Luck",
  "Resource Management",
  "Trading",
  "Modular Board",
  "Variable Player Powers",
  "Action Points",
  "Storytelling",
];

const COMPLEXITY_RANGES = [
  { key: "complexity_light", min: 0, max: 1.5 },
  { key: "complexity_light_medium", min: 1.5, max: 2.5 },
  { key: "complexity_medium", min: 2.5, max: 3.5 },
  { key: "complexity_medium_heavy", min: 3.5, max: 4.5 },
  { key: "complexity_heavy", min: 4.5, max: 5 },
];

const PLAYER_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8];

const BOX_SIZES = [
  { value: "small", key: "box_small" },
  { value: "medium", key: "box_medium" },
  { value: "large", key: "box_large" },
];

const RATING_TIERS = [
  { value: "masterpiece", label: "Šedevras · TOP 1%", min: 8.0 },
  { value: "classic", label: "Klasika · TOP 5%", min: 7.5 },
  { value: "loved", label: "Mėgstamas · TOP 15%", min: 7.0 },
  { value: "solid", label: "Solidus", min: 6.5 },
] as const;

const SORT_OPTION_VALUES = [
  "popularity",
  "biggest_drop",
  "all_time_low",
  "lowest_price",
  "title_asc",
  "title_desc",
  "rating",
  "newest",
] as const;

const PAGE_SIZE = 24;

export function GamesPage() {
  const { t } = useTranslation();
  usePageTitle(t("games_page.page_title"));

  const SORT_OPTIONS = SORT_OPTION_VALUES.map((value) => ({
    value,
    label: t(`sort.${value === "popularity" ? "default" : value}`),
  }));

  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (!v || v === "0" && k === "page") next.delete(k);
          else next.set(k, v);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const search = searchParams.get("q") || "";
  const genre = searchParams.get("genre") || "";
  const mechanic = searchParams.get("mechanic") || "";
  const playerCount = searchParams.get("players") || "";
  const complexityIdx = searchParams.get("complexity") || "";
  const ratingTier = searchParams.get("ratingTier") || "";
  const languageIndependent = searchParams.get("langIndep") === "1";
  const boxSize = searchParams.get("boxSize") || "";
  const minPrice = searchParams.get("minPrice") || "";
  const maxPrice = searchParams.get("maxPrice") || "";
  const inStock = searchParams.get("inStock") === "1";
  const shopsParam = searchParams.get("shops") || "";
  const selectedShops = useMemo(
    () => (shopsParam ? shopsParam.split(",").filter(Boolean) : []),
    [shopsParam],
  );
  const sortBy = searchParams.get("sort") || "popularity";
  const page = Number(searchParams.get("page") || "0");

  const commitSearch = useCallback((v: string) => updateParams({ q: v, page: "" }), [updateParams]);
  const setGenre = useCallback((v: string) => updateParams({ genre: v, page: "" }), [updateParams]);
  const setMechanic = useCallback((v: string) => updateParams({ mechanic: v, page: "" }), [updateParams]);
  const setPlayerCount = useCallback((v: number | string) => updateParams({ players: String(v), page: "" }), [updateParams]);
  const setComplexityIdx = useCallback((v: number | string) => updateParams({ complexity: String(v), page: "" }), [updateParams]);
  const setRatingTier = useCallback((v: string) => updateParams({ ratingTier: v, minRating: "", maxRating: "", page: "" }), [updateParams]);
  const setLanguageIndependent = useCallback((v: boolean) => updateParams({ langIndep: v ? "1" : "", page: "" }), [updateParams]);
  const setBoxSize = useCallback((v: string) => updateParams({ boxSize: v, page: "" }), [updateParams]);
  const setMinPrice = useCallback((v: string) => updateParams({ minPrice: v, page: "" }), [updateParams]);
  const setMaxPrice = useCallback((v: string) => updateParams({ maxPrice: v, page: "" }), [updateParams]);
  const setInStock = useCallback((v: boolean) => updateParams({ inStock: v ? "1" : "", page: "" }), [updateParams]);
  const toggleShop = useCallback(
    (shop: string) => {
      const next = selectedShops.includes(shop)
        ? selectedShops.filter((s) => s !== shop)
        : [...selectedShops, shop];
      updateParams({ shops: next.join(","), page: "" });
    },
    [selectedShops, updateParams],
  );
  const setSortBy = useCallback((v: string) => updateParams({ sort: v === "popularity" ? "" : v, page: "" }), [updateParams]);
  const setPage = useCallback((v: number | ((p: number) => number)) => {
    const newPage = typeof v === "function" ? v(page) : v;
    updateParams({ page: newPage ? String(newPage) : "" });
    window.scrollTo({ top: 0 });
  }, [updateParams, page]);

  const [searchInput, setSearchInput] = useState(search);
  const debouncedInput = useDebouncedValue(searchInput);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const complexityRange =
    complexityIdx !== "" ? COMPLEXITY_RANGES[Number(complexityIdx)] : null;
  const selectedRatingTier =
    RATING_TIERS.find((tier) => tier.value === ratingTier) ?? null;

  const { data, isLoading } = useQuery({
    queryKey: [
      "games",
      search,
      genre,
      mechanic,
      playerCount,
      complexityIdx,
      ratingTier,
      languageIndependent,
      boxSize,
      minPrice,
      maxPrice,
      inStock,
      shopsParam,
      sortBy,
      page,
    ],
    queryFn: () =>
      graphqlClient.request<{ games: Game[]; availableShops: string[] }>(GAMES_QUERY, {
        query: search || undefined,
        genre: genre || undefined,
        mechanic: mechanic || undefined,
        playerCount: playerCount ? Number(playerCount) : undefined,
        minComplexity: complexityRange?.min ?? undefined,
        maxComplexity: complexityRange?.max ?? undefined,
        minRating: selectedRatingTier?.min,
        maxRating: undefined,
        languageIndependent: languageIndependent || undefined,
        boxSize: boxSize || undefined,
        minPrice: minPrice ? Number(minPrice) : undefined,
        maxPrice: maxPrice ? Number(maxPrice) : undefined,
        inStock: inStock || undefined,
        shops: selectedShops.length > 0 ? selectedShops : undefined,
        sortBy,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const availableShops = data?.availableShops ?? [];

  const { data: suggestionsData } = useQuery({
    queryKey: ["gameSuggestions", debouncedInput],
    queryFn: () =>
      graphqlClient.request<{ searchGames: Pick<Game, "id" | "title" | "thumbnailUrl">[] }>(
        GAME_SUGGESTIONS_QUERY,
        { query: debouncedInput }
      ),
    enabled: debouncedInput.length >= 2 && showSuggestions,
  });

  const suggestions = (suggestionsData?.searchGames ?? []).slice(0, 5);

  const games = data?.games ?? [];
  const hasMore = games.length === PAGE_SIZE;

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const clearPanelFilters = useCallback(() => {
    updateParams({
      genre: "",
      mechanic: "",
      players: "",
      complexity: "",
      ratingTier: "",
      minRating: "",
      maxRating: "",
      langIndep: "",
      boxSize: "",
      minPrice: "",
      maxPrice: "",
      inStock: "",
      shops: "",
      page: "",
    });
  }, [updateParams]);

  const hasFilters =
    search || genre || mechanic || playerCount !== "" || complexityIdx !== "" || languageIndependent || boxSize ||
    ratingTier || minPrice || maxPrice || inStock || selectedShops.length > 0 || sortBy !== "popularity";

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (genre) count++;
    if (mechanic) count++;
    if (playerCount !== "") count++;
    if (complexityIdx !== "") count++;
    if (ratingTier) count++;
    if (languageIndependent) count++;
    if (boxSize) count++;
    if (minPrice || maxPrice) count++;
    if (inStock) count++;
    if (selectedShops.length > 0) count++;
    return count;
  }, [genre, mechanic, playerCount, complexityIdx, ratingTier, languageIndependent, boxSize, minPrice, maxPrice, inStock, selectedShops]);

  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; onRemove: () => void }[] = [];
    if (genre) {
      pills.push({ key: "genre", label: t(`genres.${genre}`, genre), onRemove: () => { setGenre(""); } });
    }
    if (mechanic) {
      pills.push({ key: "mechanic", label: t(`mechanics.${mechanic}`, mechanic), onRemove: () => { setMechanic(""); } });
    }
    if (playerCount !== "") {
      pills.push({
        key: "playerCount",
        label: t("games_page.players_count", { count: Number(playerCount) }),
        onRemove: () => { setPlayerCount(""); },
      });
    }
    if (complexityIdx !== "") {
      pills.push({
        key: "complexity",
        label: t(`games_page.${COMPLEXITY_RANGES[Number(complexityIdx)].key}`),
        onRemove: () => { setComplexityIdx(""); },
      });
    }
    if (ratingTier) {
      const tier = RATING_TIERS.find((entry) => entry.value === ratingTier);
      pills.push({
        key: "ratingTier",
        label: t("games_page.pill_rating_tier", { label: tier?.label ?? ratingTier }),
        onRemove: () => { setRatingTier(""); },
      });
    }
    if (languageIndependent) {
      pills.push({
        key: "langIndep",
        label: t("games_page.filter_language_indep"),
        onRemove: () => { setLanguageIndependent(false); },
      });
    }
    if (boxSize) {
      const sizeEntry = BOX_SIZES.find((s) => s.value === boxSize);
      const label = sizeEntry ? t(`games_page.${sizeEntry.key}`) : boxSize;
      pills.push({
        key: "boxSize",
        label: t("games_page.pill_box_size", { label }),
        onRemove: () => { setBoxSize(""); },
      });
    }
    if (minPrice && maxPrice) {
      pills.push({
        key: "priceRange",
        label: t("games_page.pill_price_range", { min: minPrice, max: maxPrice }),
        onRemove: () => { setMinPrice(""); setMaxPrice(""); },
      });
    } else if (minPrice) {
      pills.push({
        key: "minPrice",
        label: t("games_page.pill_price_min", { min: minPrice }),
        onRemove: () => { setMinPrice(""); },
      });
    } else if (maxPrice) {
      pills.push({
        key: "maxPrice",
        label: t("games_page.pill_price_max", { max: maxPrice }),
        onRemove: () => { setMaxPrice(""); },
      });
    }
    if (inStock) {
      pills.push({
        key: "inStock",
        label: t("games_page.pill_in_stock"),
        onRemove: () => { setInStock(false); },
      });
    }
    if (selectedShops.length > 0) {
      pills.push({
        key: "shops",
        label: t("games_page.pill_shops", { count: selectedShops.length }),
        onRemove: () => { updateParams({ shops: "", page: "" }); },
      });
    }
    return pills;
  }, [genre, mechanic, playerCount, complexityIdx, ratingTier, languageIndependent, boxSize, minPrice, maxPrice, inStock, selectedShops, t, setGenre, setMechanic, setPlayerCount, setComplexityIdx, setRatingTier, setLanguageIndependent, setBoxSize, setMinPrice, setMaxPrice, setInStock, updateParams]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t("games_page.page_title")}
        </h1>
        <p className="mt-2 text-gray-600">{t("games_page.subtitle")}</p>
      </div>

      {/* Search + toolbar */}
      <div className="mb-8 space-y-3">
        {/* Search bar */}
        <div className="relative flex" ref={suggestionsRef}>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setShowSuggestions(true);
              setHighlightedIndex(-1);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" && showSuggestions && suggestions.length > 0) {
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % suggestions.length);
              } else if (e.key === "ArrowUp" && showSuggestions && suggestions.length > 0) {
                e.preventDefault();
                setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                if (highlightedIndex >= 0 && showSuggestions && suggestions.length > 0) {
                  const selected = suggestions[highlightedIndex].title;
                  setSearchInput(selected);
                  commitSearch(selected);
                } else {
                  commitSearch(searchInput);
                }
                setShowSuggestions(false);
              } else if (e.key === "Escape") {
                setShowSuggestions(false);
              }
            }}
            onBlur={(e) => {
              if (!suggestionsRef.current?.contains(e.relatedTarget)) {
                setShowSuggestions(false);
              }
            }}
            placeholder={t("games_page.search_example_placeholder")}
            className="input min-w-0 flex-1 rounded-r-none border-r-0"
          />
          <button
            type="button"
            onClick={() => {
              commitSearch(searchInput);
              setShowSuggestions(false);
            }}
            className="inline-flex items-center gap-1.5 rounded-l-none rounded-r-lg border border-primary-800 bg-primary-800 px-4 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            aria-label={t("games_page.search_button")}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {t("games_page.search_button")}
          </button>
          {showSuggestions && suggestions.length > 0 && searchInput.length >= 2 && (
            <div className="absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
              {suggestions.map((game, index) => (
                <button
                  key={game.id}
                  className={`w-full px-4 py-2.5 text-left text-sm text-gray-700 ${index === highlightedIndex ? "bg-gray-100" : "hover:bg-gray-50"}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => {
                    setSearchInput(game.title);
                    commitSearch(game.title);
                    setShowSuggestions(false);
                  }}
                >
                  {game.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inline essentials: Price · In-stock · Sort + More-filters disclosure */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Price range inline */}
          <div className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1">
            <input
              type="number"
              inputMode="decimal"
              min="0"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder={t("games_page.min_price_placeholder")}
              className="w-16 border-0 bg-transparent p-1 text-sm focus:outline-none focus:ring-0"
              aria-label={t("games_page.min_price_placeholder")}
            />
            <span className="text-gray-400">–</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder={t("games_page.max_price_placeholder")}
              className="w-16 border-0 bg-transparent p-1 text-sm focus:outline-none focus:ring-0"
              aria-label={t("games_page.max_price_placeholder")}
            />
          </div>

          {/* In-stock toggle inline */}
          <button
            type="button"
            onClick={() => setInStock(!inStock)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              inStock
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
            }`}
            aria-pressed={inStock}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                inStock ? "bg-emerald-600" : "bg-gray-300"
              }`}
            />
            {t("games_page.inline_filter_in_stock")}
          </button>

          {/* More filters disclosure */}
          <button
            onClick={() => setFiltersOpen((prev) => !prev)}
            className={`
              relative inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium
              transition-all duration-200
              ${filtersOpen
                ? "border-primary-300 bg-primary-50 text-primary-800"
                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"
              }
            `}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            {filtersOpen ? t("games_page.fewer_filters") : t("games_page.more_filters")}
            {activeFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary-800 px-1.5 text-xs font-semibold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          {hasFilters && (
            <button
              onClick={resetFilters}
              className="rounded-lg px-2 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            >
              {t("games_page.clear_all")}
            </button>
          )}

          {/* Sort pushed to the right */}
          <div className="ml-auto flex items-center gap-1.5">
            <svg
              className="h-4 w-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h6M3 12h10M3 17h14M19 7l-3-3m0 0l-3 3m3-3v14" />
            </svg>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-1 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Collapsible filter panel */}
        <div
          className={`
            grid transition-all duration-300 ease-in-out
            ${filtersOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}
          `}
        >
          <div className="overflow-hidden">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              {/* Filter grid — advanced filters live here; price and in-stock are inline above */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("games_page.filter_players")}
                  </label>
                  <select
                    value={playerCount}
                    onChange={(e) => setPlayerCount(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t("games_page.filter_all")}</option>
                    {PLAYER_COUNTS.map((count) => (
                      <option key={count} value={count}>
                        {t("games_page.players_count", { count })}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("games_page.filter_complexity")}
                  </label>
                  <select
                    value={complexityIdx}
                    onChange={(e) => setComplexityIdx(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t("games_page.filter_all")}</option>
                    {COMPLEXITY_RANGES.map((range, idx) => (
                      <option key={idx} value={idx}>
                        {t(`games_page.${range.key}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("games_page.filter_rating")}
                  </label>
                  <select
                    value={ratingTier}
                    onChange={(e) => setRatingTier(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t("games_page.filter_all")}</option>
                    {RATING_TIERS.map((tier) => (
                      <option key={tier.value} value={tier.value}>
                        {tier.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("games_page.filter_genre")}
                  </label>
                  <select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t("games_page.filter_all")}</option>
                    {GENRES.map((g) => (
                      <option key={g} value={g}>{t(`genres.${g}`, g)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("games_page.filter_mechanic")}
                  </label>
                  <select
                    value={mechanic}
                    onChange={(e) => setMechanic(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t("games_page.filter_all_feminine")}</option>
                    {MECHANICS.map((m) => (
                      <option key={m} value={m}>{t(`mechanics.${m}`, m)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("games_page.filter_box_size")}
                  </label>
                  <select
                    value={boxSize}
                    onChange={(e) => setBoxSize(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t("games_page.filter_all")}</option>
                    {BOX_SIZES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {t(`games_page.${s.key}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end pb-2">
                  <label className="flex cursor-pointer items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={languageIndependent}
                      onChange={(e) => setLanguageIndependent(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-800 focus:ring-primary-800"
                    />
                    <span className="text-sm text-gray-700">
                      {t("games_page.filter_language_indep")}
                    </span>
                  </label>
                </div>

                {availableShops.length > 0 && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("games_page.filter_shops")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {availableShops.map((shop) => {
                        const active = selectedShops.includes(shop);
                        return (
                          <button
                            key={shop}
                            type="button"
                            onClick={() => toggleShop(shop)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                              active
                                ? "border-primary-800 bg-primary-800 text-white"
                                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                            }`}
                          >
                            {shop}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
                {activeFilterCount > 0 ? (
                  <button
                    onClick={clearPanelFilters}
                    className="text-sm font-medium text-red-600 transition-colors hover:text-red-700"
                  >
                    {t("games_page.clear_filters_count", { count: activeFilterCount })}
                  </button>
                ) : (
                  <span className="text-sm text-gray-400">
                    {t("games_page.no_filters_active")}
                  </span>
                )}
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                  </svg>
                  {t("games_page.close_panel")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Active filter pills (shown when panel is closed) */}
        {!filtersOpen && activeFilterPills.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterPills.map((pill) => (
              <span
                key={pill.key}
                className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 py-1 pl-3 pr-1.5 text-sm font-medium text-primary-800"
              >
                {pill.label}
                <button
                  onClick={pill.onRemove}
                  className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-primary-600 transition-colors hover:bg-primary-200 hover:text-primary-800"
                  aria-label={`Pašalinti filtrą: ${pill.label}`}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
            <button
              onClick={clearPanelFilters}
              className="text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
            >
              Išvalyti viską
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-800 border-t-transparent" />
        </div>
      ) : games.length === 0 ? (
        <div className="py-20 text-center">
          <p className="text-lg text-gray-500">Žaidimų nerasta</p>
          {hasFilters && (
            <button
              onClick={resetFilters}
              className="mt-4 text-sm font-medium text-primary-800 hover:text-primary-700"
            >
              Išvalyti filtrus
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {games.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-10 flex items-center justify-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Ankstesnis
            </button>
            <span className="text-sm text-gray-600">
              Puslapis {page + 1}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Kitas
            </button>
          </div>
        </>
      )}
    </div>
  );
}
